import React, { useState, useRef } from "react";
import axios from "axios";
import { DESIGNMATE } from "../../../../config/endPoints";
import { 
  EditOptionContainer, 
  EditOptionTitle, 
  EditOptionDescription, 
  FormGroup, 
  Button, 
  ButtonGroup,
  TipBox,
  InputSlider,
  RadioGroup
} from "../EditOptionStyles";
import { generativeFill } from "../../utils/editUtils";
import DrawingModal from "./DrawingModal";

const GenerativeFill = ({ onSubmit, isLoading, selectedImage, credits, setCredits, setShowPopup }) => {
  const [maskMode, setMaskMode] = useState("draw");
  const [maskFile, setMaskFile] = useState(null);
  const [generatedMask, setGeneratedMask] = useState(null);
  const [maskPreviewUrl, setMaskPreviewUrl] = useState(null);
  const [brushSize, setBrushSize] = useState(20);
  const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false);
  
  // API Parameters
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [numResults, setNumResults] = useState(1);
  
  // Update prompt loading state
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  
  const fileInputRef = useRef(null);
  
  const maskModeOptions = [
    { value: "draw", label: "Draw on Image" },
    { value: "upload", label: "Upload Mask" }
  ];

  /**
   * Function to update the prompt
   */
  const handleUpdatePrompt = async () => {
    try {
      if (!prompt) {
        return;
      }
      if (credits < 0.5) {
        setShowPopup && setShowPopup(true);
        return;
      }
      setIsUpdatingPrompt(true);
      const token = localStorage.getItem("authorization");
      const postOptions = {
        method: "POST",
        url: `${DESIGNMATE}/enhancePrompt`,
        headers: {
          accept: "application/json",
          authorization: token,
        },
        data: {
          prompt,
        },
      };
      const result = await axios(postOptions);
      if (result?.data?.responseMessage === "Insufficient Credits") {
        setShowPopup && setShowPopup(true);
        return;
      }
      setPrompt(result?.data?.enhancedPrompt);
      setCredits && setCredits(credits - 0.2);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMaskFile(file);
    }
  };

  const handleDrawingComplete = (maskBlob, previewDataUrl) => {
    setGeneratedMask(maskBlob);
    setMaskPreviewUrl(previewDataUrl || null);
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      alert("Please enter a prompt describing what to generate.");
      return;
    }

    let finalMaskFile = null;
    
    if (maskMode === "draw") {
      if (!generatedMask) {
        alert("Please draw areas where you want to generate content.");
        return;
      }
      finalMaskFile = generatedMask;
    } else {
      if (!maskFile) {
        alert("Please select a mask file to define the area for generation.");
        return;
      }
      finalMaskFile = maskFile;
    }

    // Structure options according to API documentation
    const apiOptions = {
      mask_file: finalMaskFile,
      mask_type: "manual", // Default as per API
      prompt: prompt.trim(),
      negative_prompt: negativePrompt.trim() || undefined,
      num_results: numResults
    };

    console.log("GenerativeFill - Submitting with:");
    console.log("- Selected Image:", selectedImage);
    console.log("- Mask Mode:", maskMode);
    console.log("- Mask File/Blob:", finalMaskFile);
    console.log("- API Options:", apiOptions);

    onSubmit({
      editType: "generativeFill",
      options: apiOptions,
      apiFunction: generativeFill
    });
  };

  const canSubmit =
    Boolean(selectedImage) &&
    Boolean(prompt.trim()) &&
    (maskMode === "draw" ? Boolean(generatedMask) : Boolean(maskFile));

  return (
    <>
      <EditOptionContainer>
        <div>
          <EditOptionTitle icon="🎨">Generative Fill</EditOptionTitle>
          <EditOptionDescription>
            Use AI to intelligently generate content in selected areas of your image. Draw or upload a mask to define the generation area, then describe what you want to create.
          </EditOptionDescription>
        </div>

        <FormGroup label="Mask Creation Method">
          <RadioGroup 
            options={maskModeOptions} 
            value={maskMode} 
            onChange={setMaskMode}
          />
        </FormGroup>

        {maskMode === "draw" ? (
          <FormGroup label="Drawing Tool">
            <div style={{
              marginBottom: "1rem",
              padding: "1.5rem",
              backgroundColor: generatedMask ? "#f0f5ff" : "#f8fafd",
              borderRadius: "8px",
              border: `2px dashed ${generatedMask ? "#4D7CFE" : "#dde3ed"}`,
              textAlign: "center",
              cursor: selectedImage ? "pointer" : "not-allowed"
            }} onClick={() => selectedImage && setIsDrawingModalOpen(true)}>
              {selectedImage ? (
                <div>
                  {generatedMask && maskPreviewUrl ? (
                    <img
                      src={maskPreviewUrl}
                      alt="Mask preview"
                      style={{
                        width: "100%",
                        borderRadius: "4px",
                        marginBottom: "0.5rem",
                        display: "block"
                      }}
                    />
                  ) : (
                    <div style={{
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                      color: "#666"
                    }}>
                      🎨
                    </div>
                  )}
                  <div style={{
                    color: generatedMask ? "#4D7CFE" : "#666",
                    fontWeight: "500",
                    marginBottom: "0.5rem"
                  }}>
                    {generatedMask ? "Mask Created — click to modify" : "Open Drawing Tool"}
                  </div>
                  {!generatedMask && (
                    <div style={{ color: "#888", fontSize: "0.9rem" }}>
                      Click to start drawing areas for generation
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: "#666", padding: "1rem" }}>
                  Please select an image first to start drawing
                </div>
              )}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#888" }}>
              Opens a full-screen drawing interface for precise mask creation
            </div>
          </FormGroup>
        ) : (
          <FormGroup label="Upload Mask">
            <div style={{
              marginBottom: "1rem",
              padding: "1.5rem",
              backgroundColor: maskFile ? "#f0f5ff" : "#f8fafd",
              borderRadius: "8px",
              border: `2px dashed ${maskFile ? "#4D7CFE" : "#dde3ed"}`,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }} onClick={() => fileInputRef.current?.click()}>
              {maskFile ? (
                <div>
                  <div style={{ color: "#4D7CFE", fontWeight: "500", marginBottom: "0.5rem" }}>
                    ✓ Mask Selected
                  </div>
                  <div style={{ color: "#666", fontSize: "0.9rem" }}>
                    {maskFile.name}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.8rem", marginTop: "0.25rem" }}>
                    Click to change mask
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>
                    Upload Binary Mask
                  </div>
                  <div style={{ color: "#888", fontSize: "0.9rem" }}>
                    White areas (255) will be filled with generated content
                  </div>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              style={{ display: "none" }}
            />
            <div style={{ fontSize: "0.75rem", color: "#888" }}>
              Supported formats: JPEG, JPG, PNG, WEBP. Mask must have the same aspect ratio as the input image.
            </div>
          </FormGroup>
        )}

        <FormGroup label="Generation Prompt">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what you want to generate in the selected area..."
            style={{
              width: "100%",
              padding: "0.7rem",
              borderRadius: "6px",
              border: "1px solid #dde3ed",
              minHeight: "80px",
              resize: "vertical",
              outline: "none",
              fontSize: "0.9rem",
              fontFamily: "inherit",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#4D7CFE";
              e.target.style.boxShadow = "0 0 0 3px rgba(77, 124, 254, 0.15)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#dde3ed";
              e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
            }}
          />
          <div style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.5rem" }}>
            Required: Describe what you want to generate in the masked area
          </div>
        </FormGroup>

        <FormGroup>
          <div style={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            marginBottom: "1rem" 
          }}>
            <button
              type="button"
              disabled={!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)}
              onClick={handleUpdatePrompt}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: (!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)) ? "#ccc" : "MediumSeaGreen",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: (!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)) ? "not-allowed" : "pointer",
                fontSize: "0.85rem",
                fontWeight: "500"
              }}
            >
              {isUpdatingPrompt ? "Updating..." : "Update Prompt"}
            </button>
          </div>
        </FormGroup>

        <FormGroup label="Negative Prompt (Optional)">
          <textarea
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            placeholder="Describe what you want to avoid in the generation..."
            style={{
              width: "100%",
              padding: "0.7rem",
              borderRadius: "6px",
              border: "1px solid #dde3ed",
              minHeight: "60px",
              resize: "vertical",
              outline: "none",
              fontSize: "0.9rem",
              fontFamily: "inherit",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#4D7CFE";
              e.target.style.boxShadow = "0 0 0 3px rgba(77, 124, 254, 0.15)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#dde3ed";
              e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
            }}
          />
          <div style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.5rem" }}>
            Optional: Specify details or attributes to avoid in the generated content
          </div>
        </FormGroup>

        <FormGroup label={`Number of Results: ${numResults}`}>
          <InputSlider 
            value={numResults} 
            onChange={(e) => setNumResults(parseInt(e.target.value))} 
            min={1} 
            max={2} 
          />
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            fontSize: "0.75rem", 
            color: "#888",
            marginTop: "0.25rem" 
          }}>
            <span>1 Result</span>
            <span>2 Results</span>
          </div>
        </FormGroup>

        <TipBox 
          title="Pro Tips" 
          icon="💡"
        >
          • Be specific in your prompts for better results
          • White areas (255) in the mask will be filled with new content
          • Use negative prompts to avoid unwanted elements
          • For multiple results, consider using async mode for better performance
        </TipBox>

        <ButtonGroup>
          <Button 
            primary={false} 
            outline={true}
            onClick={() => {
              setMaskFile(null);
              setGeneratedMask(null);
              setMaskPreviewUrl(null);
              setPrompt("");
              setNegativePrompt("");
              setNumResults(2);
              setBrushSize(20);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          >
            Reset
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? "Generating..." : "Generate Fill"}
          </Button>
        </ButtonGroup>
      </EditOptionContainer>

      {/* Full-screen Drawing Modal */}
      <DrawingModal
        isOpen={isDrawingModalOpen}
        onClose={() => setIsDrawingModalOpen(false)}
        selectedImage={selectedImage}
        brushSize={brushSize}
        setBrushSize={setBrushSize}
        onSave={handleDrawingComplete}
        title="Draw Areas for Generation"
        description="Paint over areas where you want to generate new content. Hold Ctrl/Cmd to pan."
      />
    </>
  );
};

export default GenerativeFill;
